import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { hamMenuRight } from '../../assets/components/svgs';
import {
  Bottom,
  BreadcumbsContainer,
  DynamicContainer,
  Location,
  MenuButton,
  MenuButtonContainer,
  BreadCrumbs,
  DynamicButtonContainer,
  Ellipsis,
  ActiveBreadcrumb
} from './MenuStyles';
import urlPaths from '../../components/constants/top-menu-constants';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { setBreadcrump } from '../../app/organizationReducer';
import { resetTemplateRoute } from '../../app/templateReducer';
import { useTheme } from '../../context/themeContext';
import { RouteConstants } from '../../constants/RouteConstants';

type Props = {
  buttons?: JSX.Element;
  breadcrumbs?: JSX.Element;
  setSidebarIsActive: React.Dispatch<React.SetStateAction<boolean>>;
  sidebarIsActive: boolean;
};

const BreadcrumbItem = ({ path, isLast }: any) => {
  return isLast ? (
    <ActiveBreadcrumb>{path.label}</ActiveBreadcrumb>
  ) : (
    <BreadcrumbLink to={path.key}>{path.label || 'Unnamed'}</BreadcrumbLink>
  );
};

const MenuBottom = ({ buttons, setSidebarIsActive, sidebarIsActive }: Props) => {
  const location = useLocation().pathname;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const storedURLPaths = useAppSelector((state) => state.organization.urlPaths);
  const [title, setTitle] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (storedURLPaths.length) {
      const pathSegments = storedURLPaths.filter((path: any) => location.startsWith(path.key));
      setTitle(pathSegments.map((segment: any) => segment.label).join(' > '));
    } else if (!storedURLPaths.length && isLoading) {
      dispatch(setBreadcrump(urlPaths));
      setIsLoading(false);
    }
  }, [storedURLPaths, location]);

  useEffect(() => {
    if (!storedURLPaths.length && isLoading) {
      const savedBreadcrumbs = localStorage.getItem('breadcrumbs');
      if (savedBreadcrumbs) {
        dispatch(setBreadcrump(JSON.parse(savedBreadcrumbs)));
      }
      setIsLoading(false);
    }
  }, [dispatch, storedURLPaths.length, isLoading]);

  let maxItems = 5;
  const itemsToShow = isExpanded
    ? storedURLPaths
    : storedURLPaths.length > maxItems
    ? [storedURLPaths[0], ...storedURLPaths.slice(-maxItems + 1)]
    : storedURLPaths;

  return (
    <Bottom>
      <MenuButtonContainer theme={useTheme().theme}>
        <MenuButton
          isActive={sidebarIsActive}
          onClick={() => setSidebarIsActive((state) => !state)}
        >
          {hamMenuRight}
          MENU
        </MenuButton>
      </MenuButtonContainer>
      <div className="flex h-full w-[97%] items-center border-b-[1.7px] pr-5">
        {storedURLPaths?.length ? (
          <DynamicContainer>
            <BreadcumbsContainer>
              <BreadCrumbs role="breadcrumbs">
                {itemsToShow.map((path: any, i: number) => (
                  <React.Fragment key={path.key}>
                    {i > 0 && i === 1 && storedURLPaths.length > 4 && !isExpanded && (
                      <Ellipsis onClick={() => setIsExpanded(true)}>...</Ellipsis>
                    )}
                    <BreadcrumbItem path={path} isLast={i === itemsToShow.length - 1} />
                    {i < itemsToShow.length - 1 && <span> &gt; </span>}
                  </React.Fragment>
                ))}
                {isExpanded && (
                  <span onClick={() => setIsExpanded(false)} className="collapse">
                    Collapse
                  </span>
                )}
              </BreadCrumbs>
            </BreadcumbsContainer>
            <DynamicButtonContainer role="buttons">{buttons}</DynamicButtonContainer>
          </DynamicContainer>
        ) : (
          <Location
            id="header-location"
            className="w-1/2 overflow-hidden text-ellipsis whitespace-nowrap"
            role="nobread"
            theme={useTheme().theme}
          >
            <Link
              id="header-title-link"
              to={
                location.split('/')[1] === '/templates'
                  ? '/templates/list'
                  : location.split('/')[1] === 'organization'
                  ? RouteConstants.ROUTE_OS_ORGANIZATION_LISTING
                  : location.split('/')[1] === 'admin'
                  ? '/admin-list'
                  : location.split('/')[1] === 'roles'
                  ? '/roles'
                  : location.split('/')[1] === 'permission'
                  ? '/permissionset-list'
                  : location.split('/')[1] === 'templates'
                  ? '/templates'
                  : location.split('/')[1] === 'metadata'
                  ? RouteConstants.ROUTE_MDS_APPLN_LIST
                  : location.split('/')[1] === 'survey-question'
                  ? '/survey-question/list'
                  : location.split('/')[1] === 'survey-template'
                  ? '/survey-template/list'
                  : location.split('/')[1] === 'config-key'
                  ? '/config-key/list'
                  : location.split('/')[1] === 'mobile-configuration'
                  ? 'mobile/configuration/list'
                  : location.split('/')[1] === 'consents' && location.split('/')[2] === 'fieldgroup'
                  ? '/consents/fieldgroup'
                  : location.split('/')[1] === 'consents'
                  ? '/consents'
                  : location
              }
              onClick={() => {
                if (location === '/templates/list' || location === '/templates/create') {
                  dispatch(resetTemplateRoute());
                  navigate('/template/list');
                } else {
                  navigate(location);
                }
              }}
              className="hover:text-primary-600 transition duration-150 ease-in-out"
              data-te-toggle="tooltip"
              data-te-placement="bottom"
              title={title}
            >
              {title !== null && !title.startsWith('T_') ? title : ''}
            </Link>
          </Location>
        )}

        <div id="header-bottom-right-container" className="flex w-1/2 flex-auto justify-end" />
      </div>
    </Bottom>
  );
};

const BreadcrumbLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

export default MenuBottom;
